<template>
    <div class="location-list">
      <div class="header">
        <h3>Locations</h3>
        <button @click="navigateToCreateLocation" class="new-location-button">
          New Location
        </button>
      </div>
  
      <div class="search-filter-row">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search by Name..."
          class="search-input"
        />
        <button @click="showFilters = !showFilters" class="toggle-filter-button">
          {{ showFilters ? 'Hide Filters' : 'Show Filters' }}
        </button>
      </div>
      <div v-if="showFilters" class="filters">
        <input type="date" v-model="startDate" class="date-input" placeholder="Start Date" />
        <input type="date" v-model="endDate" class="date-input" placeholder="End Date" />
      </div>
  
      <table class="location-table">
        <thead>
          <tr>
            <th @click="sortLocations('name')">
              Name
              <i
                v-if="currentSortColumn === 'name'"
                class="filter-arrow"
                :class="setArrowDirection(currentSortDirection)"
              ></i>
            </th>
            <th @click="sortLocations('description')">
              Description
              <i
                v-if="currentSortColumn === 'description'"
                class="filter-arrow"
                :class="setArrowDirection(currentSortDirection)"
              ></i>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="location in paginatedLocations"
            :key="location.id"
            @click="navigateToLocationDetail(location.id)"
            class="location-row"
          >
            <td>{{ location.name }}</td>
            <!-- The description cell: text is truncated via a method and CSS; full text appears in a tooltip on hover -->
            <td class="location-description-cell" v-tooltip="location.description">
              <span class="location-description">
                {{ truncateText(location.description) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
  
      <div class="pagination">
        <button
          v-for="page in visiblePageNumbers"
          :key="page"
          @click="setPage(page)"
          :class="{ active: page === currentPage }"
        >
          {{ page }}
        </button>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { API_ENDPOINTS } from '@/services/config.js';
  
  export default {
    data() {
      return {
        locations: [],
        searchQuery: '',
        currentPage: 1,
        locationsPerPage: 15,
        currentSortColumn: 'name',
        currentSortDirection: 'asc',
        showFilters: false,
        startDate: '',
        endDate: '',
        sortedLocations: [],
      };
    },
    created() {
      this.fetchLocations();
    },
    methods: {
      navigateToCreateLocation() {
        this.$router.push({ path: '/create-location' });
      },
      async fetchLocations() {
        try {
          const response = await axios.get(API_ENDPOINTS.locations);
          this.locations = response.data;
          // Create a local copy for sorting/filtering
          this.sortedLocations = [...this.locations];
          this.sortLocations(this.currentSortColumn);
        } catch (error) {
          console.error("Error fetching locations:", error);
        }
      },
      sortLocations(column) {
        if (this.currentSortColumn === column) {
          this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
        } else {
          this.currentSortDirection = 'asc';
          this.currentSortColumn = column;
        }
        this.sortedLocations.sort((a, b) => {
          if (a[column] < b[column]) return this.currentSortDirection === 'asc' ? -1 : 1;
          if (a[column] > b[column]) return this.currentSortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      },
      setPage(pageNumber) {
        this.currentPage = pageNumber;
      },
      setArrowDirection(sortDirection) {
        return sortDirection === 'desc' ? 'bi bi-arrow-down' : 'bi bi-arrow-up';
      },
      navigateToLocationDetail(locationId) {
        this.$router.push({ name: 'LocationDetailPage', params: { id: locationId } });
      },
      // Truncates text to a maximum length (default: 50 characters) and adds ellipsis if needed.
      truncateText(text, maxLength = 50) {
        if (!text) return '';
        return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
      },
    },
    computed: {
      filteredLocations() {
        return this.sortedLocations.filter(location => {
          const name = location.name?.toLowerCase() || '';
          const matchesSearchQuery = name.includes(this.searchQuery.toLowerCase());
          // Optionally, add additional filters here.
          return matchesSearchQuery;
        });
      },
      paginatedLocations() {
        const start = (this.currentPage - 1) * this.locationsPerPage;
        const end = start + this.locationsPerPage;
        return this.filteredLocations.slice(start, end);
      },
      totalPages() {
        return Math.ceil(this.filteredLocations.length / this.locationsPerPage);
      },
      visiblePageNumbers() {
        const range = 15;
        let startPage = Math.max(1, this.currentPage - Math.floor(range / 2));
        let endPage = startPage + range - 1;
        if (endPage > this.totalPages) {
          endPage = this.totalPages;
          startPage = Math.max(1, endPage - range + 1);
        }
        return Array.from({ length: (endPage - startPage + 1) }, (_, i) => startPage + i);
      },
    },
  };
  </script>
  
  <style scoped lang="scss">
  @import "@/assets/variables.scss";
  
  .location-list {
    max-width: 800px;
    margin: 20px auto;
    padding: 20px;
    background: $secondary-color;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
  
      h3 {
        color: $primary-color;
      }
  
      .new-location-button {
        padding: 8px 16px;
        background-color: $primary-color;
        color: #fff;
        border: none;
        border-radius: 5px;
        cursor: pointer;
      }
  
      .new-location-button:hover {
        background-color: darken($primary-color, 10%);
      }
    }
  
    .search-filter-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 8px 0;
    }

    .search-input {
      flex: 1;
      margin: 0;
    }

    .toggle-filter-button {
      width: auto;
      margin: 0;
      padding: 6px 10px;
      white-space: nowrap;
    }

    .location-table {
      width: 100%;
    }

    .filters {
      display: flex;
      width: 100%;
      gap: 8px;
      margin: 6px 0;
    }
  
    .location-table th {
      background-color: $form-color;
      border: 1px solid $primary-color;
      cursor: pointer;
    }
  
    .location-row {
      background-color: $secondary-color;
      cursor: pointer;
    }
  
    .location-row:hover {
      background-color: $form-color;
    }
  
    /* Styles to truncate long description text and show ellipsis */
    .location-description-cell {
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  
    .pagination {
      display: flex;
      justify-content: center;
      margin-top: 20px;
    }
  
    .pagination button {
      margin: 0 5px;
      padding: 5px 10px;
      background-color: $primary-color;
      border: 1px solid $secondary-color;
      border-radius: 5px;
      cursor: pointer;
    }
  
    .pagination button:hover,
    .pagination button.active {
      background-color: darken($primary-color, 10%);
    }
  }
  </style>
  